import { useMemo } from "react"
import * as THREE from "three"

// 2026-05-06 — Reemplazo de drei <Stars>.
//
// `drei`/Stars renderea sprites con forma de estrella de cinco puntas
// (textura interna con shader que dibuja un asterisco). Eso rompía la
// inmersión del Domo Cero: las "estrellas" se veían como un símbolo
// estilizado, no como un cielo profundo. RealStars genera N puntos
// 3D distribuidos en una cápsula esférica + shader que los pinta como
// puntitos circulares suaves con AdditiveBlending. El cielo se siente
// real porque cada punto es un pixel circular tenue, no un glifo.
//
// Cada estrella tiene brillo aleatorio (0.35-1.0) para dar variedad,
// y el tamaño se ajusta con `sizeAttenuation` simulada en el shader
// (gl_PointSize ~ 1/distance) para que las estrellas más cercanas se
// vean ligeramente más grandes que las lejanas.

interface RealStarsProps {
    /** Cantidad total de puntos a generar. */
    count: number
    /** Radio medio de la cápsula esférica donde caen los puntos. */
    radius: number
    /** Espesor radial: cada punto cae entre [radius-depth/2, radius+depth/2]. */
    depth: number
    /** Tamaño base del punto en pixels antes de aplicar sizeAttenuation. */
    size?: number
    /** Color base de las estrellas (default blanco frío). */
    color?: string
    /** Si true, las estrellas se desvanecen con la profundidad (fade tipo
     *  drei Stars). Si false, mantienen brillo constante. */
    fade?: boolean
}

export function RealStars({
    count,
    radius,
    depth,
    size = 1.6,
    color = "#ffffff",
    fade = true,
}: RealStarsProps) {
    // 2026-05-06 — BufferGeometry construida imperativamente (antes
    // se usaba el patrón JSX `<bufferGeometry><bufferAttribute…/>`).
    // El patrón declarativo de R3F a veces deja attributes con
    // counts inconsistentes entre re-renders, lo que dispara
    // GL_INVALID_OPERATION (vertex buffer not big enough). Hacerlo
    // imperativo dentro de un useMemo garantiza que ambos attributes
    // se construyen juntos y se mantienen consistentes.
    const geometry = useMemo(() => {
        const positions = new Float32Array(count * 3)
        const brightness = new Float32Array(count)
        for (let i = 0; i < count; i++) {
            const theta = Math.random() * Math.PI * 2
            const phi = Math.acos(2 * Math.random() - 1)
            const r = radius - depth / 2 + Math.random() * depth
            positions[i * 3] = r * Math.sin(phi) * Math.cos(theta)
            positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta)
            positions[i * 3 + 2] = r * Math.cos(phi)
            // Distribución no uniforme de brillo: mayoría tenues, unas
            // pocas brillantes. Eleva al cubo el random uniforme y
            // mapea a 0.35-1.0 para favorecer valores bajos.
            const u = Math.random()
            brightness[i] = 0.35 + Math.pow(u, 3) * 0.65
        }
        const geo = new THREE.BufferGeometry()
        geo.setAttribute(
            "position",
            new THREE.BufferAttribute(positions, 3)
        )
        geo.setAttribute(
            "aBrightness",
            new THREE.BufferAttribute(brightness, 1)
        )
        return geo
    }, [count, radius, depth])

    const material = useMemo(() => {
        return new THREE.ShaderMaterial({
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending,
            uniforms: {
                uSize: { value: size },
                uColor: { value: new THREE.Color(color) },
                uFade: { value: fade ? 1 : 0 },
            },
            vertexShader: `
                attribute float aBrightness;
                uniform float uSize;
                varying float vBrightness;
                void main() {
                    vBrightness = aBrightness;
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                    gl_PointSize = uSize * aBrightness * (300.0 / -mvPosition.z);
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                uniform vec3 uColor;
                uniform float uFade;
                varying float vBrightness;
                void main() {
                    vec2 c = gl_PointCoord - 0.5;
                    float d = length(c);
                    if (d > 0.5) discard;
                    float alpha = smoothstep(0.5, 0.0, d);
                    float a = alpha * mix(1.0, vBrightness, uFade);
                    gl_FragColor = vec4(uColor * vBrightness, a);
                }
            `,
        })
    }, [size, color, fade])

    return <points geometry={geometry} material={material} />
}
